import {
  CONTENT_HIGHLIGHTS_ENTRY_SOURCE_HIGHLIGHTS_DIRECT,
} from './entry-source'

/** @typedef {'control' | 'test_sage_top' | 'test_unified'} UnifiedSearchWebVariant */

export const UNIFIED_SEARCH_WEB_VARIANT_CONTROL = 'control'
export const UNIFIED_SEARCH_WEB_VARIANT_SAGE_TOP = 'test_sage_top'
export const UNIFIED_SEARCH_WEB_VARIANT_UNIFIED = 'test_unified'

export const UNIFIED_SEARCH_WEB_VARIANTS = [
  UNIFIED_SEARCH_WEB_VARIANT_CONTROL,
  UNIFIED_SEARCH_WEB_VARIANT_SAGE_TOP,
  UNIFIED_SEARCH_WEB_VARIANT_UNIFIED,
]

/** APP_ENV values that dogfood unified layout (bypass PostHog bucketing). */
export const UNIFIED_SEARCH_DOGFOOD_APP_ENVS = ['stage', 'test', 'development']

/** @deprecated Prefer UNIFIED_SEARCH_DOGFOOD_APP_ENVS */
export const UNIFIED_SEARCH_STAGE_APP_ENV = 'stage'

/**
 * Stage and local dev dogfood the shipping unified layout; production runs PostHog A/B/C.
 */
export function isUnifiedSearchDogfoodEnvironment (appEnv) {
  return UNIFIED_SEARCH_DOGFOOD_APP_ENVS.includes(appEnv)
}

export function isUnifiedSearchStageEnvironment (appEnv) {
  return appEnv === UNIFIED_SEARCH_STAGE_APP_ENV
}

/**
 * Dev-only arm override (AP-19220): `?usw=<variant>` switches the effective
 * arm on DOGFOOD TIERS ONLY (development/test/stage) so arms A/B are testable
 * there — the dogfood default otherwise always forces `test_unified`. The
 * override is read at this single resolver seam so the results page and the
 * header dropdown switch arms together, and it persists in sessionStorage
 * after the first read so navigation keeps the arm for the session (clear the
 * tab session or pass a different `?usw=` to change it). In production this
 * branch is never reached, so the param is completely inert.
 *
 * Accepted values: the three arms (control | test_sage_top | test_unified)
 * plus `posthog` — the real-bucketing passthrough mode below.
 */
export const UNIFIED_SEARCH_DOGFOOD_OVERRIDE_PARAM = 'usw'

/**
 * `?usw=posthog` (dogfood tiers only, AP-19220): skip the dogfood default and
 * resolve the REAL bootstrapped PostHog variant exactly as production would —
 * a variant string, or undefined when the user isn't served, which flows
 * through as "not in experiment" → legacy experience. This makes the full
 * chain observable pre-prod: PostHog evaluation → cohort/overrides →
 * bootstrap → resolver → dropdown + results arm. Precedence: an explicit arm
 * value beats posthog mode (the latest `?usw=` always wins the single
 * override slot); posthog mode beats the dogfood default.
 */
export const UNIFIED_SEARCH_DOGFOOD_POSTHOG_MODE = 'posthog'

const DOGFOOD_OVERRIDE_VALUES = [
  ...UNIFIED_SEARCH_WEB_VARIANTS,
  UNIFIED_SEARCH_DOGFOOD_POSTHOG_MODE,
]

const DOGFOOD_OVERRIDE_STORAGE_KEY = 'unified-search-usw-override'

function readDogfoodVariantOverride () {
  if (typeof window === 'undefined') {
    return undefined
  }
  try {
    const params = new URLSearchParams(window.location.search)
    const fromQuery = params.get(UNIFIED_SEARCH_DOGFOOD_OVERRIDE_PARAM)
    if (fromQuery && DOGFOOD_OVERRIDE_VALUES.includes(fromQuery)) {
      window.sessionStorage?.setItem(DOGFOOD_OVERRIDE_STORAGE_KEY, fromQuery)
      return fromQuery
    }
    const stored = window.sessionStorage?.getItem(DOGFOOD_OVERRIDE_STORAGE_KEY)
    if (stored && DOGFOOD_OVERRIDE_VALUES.includes(stored)) {
      return stored
    }
  } catch (err) {
    // Storage disabled (private mode / hardened browser) — fall through.
  }
  return undefined
}

/**
 * Effective unified-search-web variant for UI. Stage ignores PostHog bucketing.
 * Dogfood precedence: explicit `?usw=` arm > `?usw=posthog` (real PostHog
 * passthrough) > dogfood default (`test_unified`).
 */
export function resolveUnifiedSearchWebVariant ({
  posthogVariant,
  appEnv,
}) {
  if (isUnifiedSearchDogfoodEnvironment(appEnv)) {
    const override = readDogfoodVariantOverride()
    if (override === UNIFIED_SEARCH_DOGFOOD_POSTHOG_MODE) {
      // Real bootstrapped evaluation — exactly the production expression
      // below, so an unserved user resolves undefined → legacy experience.
      return typeof posthogVariant === 'string' ? posthogVariant : undefined
    }
    return override || UNIFIED_SEARCH_WEB_VARIANT_UNIFIED
  }
  return typeof posthogVariant === 'string' ? posthogVariant : undefined
}

/**
 * Independent dev/dogfood toggle for the search/overview DERIVE path (AP-19493):
 * `?derive=1` makes /search subscribe to the overview with `deriveContext:true` (and
 * forces `skipOverview:true` on the tile search so only the derive writes the turn) on
 * DOGFOOD TIERS ONLY (development/test/stage). Orthogonal to `?usw=` — any arm can be
 * tested with or without derive. Persists in sessionStorage after the first read; pass
 * `?derive=0` to turn it back off. In production this branch is never reached, so the
 * config flag is the sole lever and the param is inert. The server ALSO double-gates on
 * `features.search.overviewDeriveEnabled`, so the client only decides whether to OFFER it.
 */
export const SEARCH_OVERVIEW_DERIVE_OVERRIDE_PARAM = 'derive'

const DERIVE_OVERRIDE_STORAGE_KEY = 'search-overview-derive-override'
const DERIVE_OVERRIDE_TRUE_VALUES = ['1', 'on', 'true']
const DERIVE_OVERRIDE_FALSE_VALUES = ['0', 'off', 'false']

function readSearchOverviewDeriveOverride () {
  if (typeof window === 'undefined') {
    return undefined
  }
  try {
    const params = new URLSearchParams(window.location.search)
    const fromQuery = params.get(SEARCH_OVERVIEW_DERIVE_OVERRIDE_PARAM)
    if (fromQuery != null) {
      if (DERIVE_OVERRIDE_TRUE_VALUES.includes(fromQuery)) {
        window.sessionStorage?.setItem(DERIVE_OVERRIDE_STORAGE_KEY, '1')
        return true
      }
      if (DERIVE_OVERRIDE_FALSE_VALUES.includes(fromQuery)) {
        window.sessionStorage?.setItem(DERIVE_OVERRIDE_STORAGE_KEY, '0')
        return false
      }
    }
    const stored = window.sessionStorage?.getItem(DERIVE_OVERRIDE_STORAGE_KEY)
    if (stored === '1') return true
    if (stored === '0') return false
  } catch (err) {
    // Storage disabled (private mode / hardened browser) — fall through.
  }
  return undefined
}

/**
 * Whether /search should offer the DERIVE overview path. Dogfood tiers honor the
 * `?derive=` URL override (persisted), falling back to the config flag; production uses
 * the config flag only (param inert). The client only decides whether to SEND
 * `deriveContext:true` — the server still gates on its own `overviewDeriveEnabled` flag.
 */
export function resolveSearchOverviewDeriveMode ({ configEnabled, appEnv }) {
  if (isUnifiedSearchDogfoodEnvironment(appEnv)) {
    const override = readSearchOverviewDeriveOverride()
    if (typeof override === 'boolean') {
      return override
    }
    return Boolean(configEnabled)
  }
  return Boolean(configEnabled)
}

/**
 * unified-search-web experiment_exposure and analytics run in production only.
 */
export function shouldRecordUnifiedSearchExperimentExposure (appEnv) {
  return !isUnifiedSearchDogfoodEnvironment(appEnv)
}

const LEGACY_EXPERIENCE = {
  mode: 'legacy',
  showHighlights: false,
  showUnifiedLayout: false,
  sagePlacement: 'none',
  skipAiSearchBrowseSummary: true,
  enrollUnifiedSearchExperiment: false,
}

/**
 * Whether an entitled user on /search can be enrolled in unified-search-web.
 */
export function isUnifiedSearchExperimentEligible ({
  helmEnabled,
  hasAccess,
  query,
  entrySource,
}) {
  if (entrySource === CONTENT_HIGHLIGHTS_ENTRY_SOURCE_HIGHLIGHTS_DIRECT) {
    return false
  }
  return Boolean(helmEnabled)
    && Boolean(hasAccess)
    && Boolean(String(query || '').trim())
}

/**
 * Map PostHog unified-search-web variant (+ gates) to global search UI behavior.
 *
 * skipAiSearchBrowseSummary gates the Sage AiGuide browse prose on /search tile
 * search (useAiSearchStream). Content Highlights transcript summary is separate.
 *
 * @param {{
 *   variant: string | undefined | null
 *   hasAccess: boolean
 *   query: string | undefined | null
 *   helmEnabled: boolean
 *   entrySource?: string | null
 * }} options
 */
export function resolveGlobalSearchExperience ({
  variant,
  hasAccess,
  query,
  helmEnabled,
  entrySource,
}) {
  if (!isUnifiedSearchExperimentEligible({
    helmEnabled,
    hasAccess,
    query,
    entrySource,
  })) {
    return { ...LEGACY_EXPERIENCE }
  }

  switch (variant) {
    case UNIFIED_SEARCH_WEB_VARIANT_UNIFIED:
      return {
        mode: 'unified',
        showHighlights: true,
        showUnifiedLayout: true,
        sagePlacement: 'none',
        skipAiSearchBrowseSummary: true,
        enrollUnifiedSearchExperiment: true,
      }
    case UNIFIED_SEARCH_WEB_VARIANT_SAGE_TOP:
      return {
        mode: 'legacy',
        showHighlights: false,
        showUnifiedLayout: false,
        sagePlacement: 'top',
        skipAiSearchBrowseSummary: false,
        enrollUnifiedSearchExperiment: true,
      }
    default:
      return {
        mode: 'legacy',
        showHighlights: false,
        showUnifiedLayout: false,
        sagePlacement: 'none',
        skipAiSearchBrowseSummary: true,
        enrollUnifiedSearchExperiment: true,
      }
  }
}

/**
 * Sage AiGuide browse summary at top of classic /search (arm B or non-entitled ai-search-web test).
 */
export function shouldShowAiSearchBrowseAtTop ({
  experience,
  aiSearchVariant,
}) {
  if (experience.showUnifiedLayout) {
    return false
  }
  if (experience.sagePlacement === 'top') {
    return true
  }
  if (experience.enrollUnifiedSearchExperiment) {
    return false
  }
  return aiSearchVariant === 'test'
}

/** @deprecated Use shouldShowAiSearchBrowseAtTop */
export const shouldShowSageOverviewAtTop = shouldShowAiSearchBrowseAtTop
